import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Settings as SettingsIcon, Shield, Plus, Pencil, Trash2, Sparkles, Download, Lock, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AVAILABLE_ICONS = ["star", "sparkles", "package", "shopping-cart", "file-text", "users", "landmark", "wallet", "book-open", "phone", "message-circle", "globe", "gift", "trending-up", "help-circle"];

export default function SystemSettings() {
  const [settings, setSettings] = useState({
    show_install_banner: true, enable_ai_chat: true, enable_whatsapp: true, enable_camera_scan: true,
    app_name: "محاسبتي", support_phone: "", support_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [downloadingSrc, setDownloadingSrc] = useState(false);
  const [backups, setBackups] = useState({ company: [], full: [], interval_hours: 3, keep: 3 });
  const [bkBusy, setBkBusy] = useState(false);
  const { user: authUser } = useAuth();
  const isRoot = !!authUser?.is_root;

  const loadBackups = async () => { try { const { data } = await api.get("/backups"); setBackups({ company: [], full: [], interval_hours: 3, keep: 3, ...(data || {}) }); } catch {} };
  useEffect(() => { loadBackups(); }, []);
  const runBackup = async (scope) => {
    setBkBusy(true);
    try { await api.post(`/backups/run?scope=${scope}`); toast.success("تم إنشاء النسخة الاحتياطية"); loadBackups(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBkBusy(false); }
  };
  const downloadBackup = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = b.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const restoreBackup = async (b) => {
    if (!window.confirm(`استعادة بيانات الشركة من نسخة ${new Date(b.created_at).toLocaleString("ar-EG")}؟\nسيتم استبدال البيانات الحالية للشركة بالكامل. لا يمكن التراجع.`)) return;
    setBkBusy(true);
    try { const { data } = await api.post(`/backups/${b.id}/restore`); toast.success(`تمت الاستعادة (${data.restored_docs} سجل)`); }
    catch (e) { toast.error(formatErr(e)); } finally { setBkBusy(false); }
  };
  const fmtSize = (n) => n > 1048576 ? `${(n/1048576).toFixed(1)}MB` : `${Math.round(n/1024)}KB`;

  const [items, setItems] = useState([]);
  const [itemOpen, setItemOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [itemForm, setItemForm] = useState({ label: "", icon: "star", url: "", note: "", order: 0 });
  const [featureAccess, setFeatureAccess] = useState({ catalog: [], companies: [] });
  const [featureForm, setFeatureForm] = useState({ company_id: "", mode: "plan", enabled: [] });
  const [featureSaving, setFeatureSaving] = useState(false);
  const canManageFeatures = authUser?.role === "super_admin";

  const load = async () => {
    try {
      const requests = [api.get("/system/settings"), api.get("/system/menu-items")];
      if (canManageFeatures) requests.push(api.get("/system/feature-access"));
      const [s, m, fa] = await Promise.all(requests);
      setSettings(sx => ({...sx, ...s.data}));
      setItems(m.data);
      if (fa?.data) {
        setFeatureAccess(fa.data);
        const selected = fa.data.companies?.find(c => c.id === featureForm.company_id) || fa.data.companies?.[0];
        if (selected) {
          const mode = selected.override?.mode || "plan";
          setFeatureForm({ company_id: selected.id, mode, enabled: mode === "custom" ? (selected.override?.enabled || []) : (selected.plan_features || []) });
        }
      }
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const selectFeatureCompany = (company_id) => {
    const c = featureAccess.companies.find(x => x.id === company_id);
    const mode = c?.override?.mode || "plan";
    setFeatureForm({ company_id, mode, enabled: mode === "custom" ? (c?.override?.enabled || []) : (c?.plan_features || []) });
  };
  const selectFeatureMode = (mode) => setFeatureForm(f => {
    if (mode !== "custom" || f.mode === "custom") return { ...f, mode };
    const company = featureAccess.companies.find(c => c.id === f.company_id);
    const enabled = f.mode === "all" ? featureAccess.catalog.map(x => x.code) : (company?.plan_features || []);
    return { ...f, mode, enabled };
  });
  const saveFeatureAccess = async () => {
    if (!featureForm.company_id) return toast.error("اختر الشركة أولاً");
    setFeatureSaving(true);
    try {
      await api.put("/system/feature-access", {
        company_id: featureForm.company_id,
        mode: featureForm.mode,
        enabled: featureForm.mode === "custom" ? featureForm.enabled : [],
      });
      toast.success("تم تحديث إعدادات ميزات الشركة");
      window.dispatchEvent(new Event("company-features-updated"));
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setFeatureSaving(false); }
  };
  const toggleFeature = (code) => setFeatureForm(f => ({ ...f, enabled: f.enabled.includes(code) ? f.enabled.filter(x => x !== code) : [...f.enabled, code] }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/system/settings", settings); toast.success("تم حفظ الإعدادات"); }
    catch(e){ toast.error(formatErr(e)); }  finally { setSaving(false); }
  };

  const openNew = () => { setEditing(null); setItemForm({ label:"", icon:"star", url:"", note:"", order: items.length }); setItemOpen(true); };
  const openEdit = (it) => { setEditing(it); setItemForm({...it}); setItemOpen(true); };
  const saveItem = async () => {
    if (!itemForm.label.trim()) return toast.error("العنوان مطلوب");
    try {
      const body = {...itemForm, order: +itemForm.order || 0};
      if (editing) await api.put(`/system/menu-items/${editing.id}`, body);
      else await api.post("/system/menu-items", body);
      toast.success("تم الحفظ");
      setItemOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };
  const delItem = async (id) => {
    if (!confirm("حذف الخيار؟")) return;
    try { await api.delete(`/system/menu-items/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const downloadSourceCode = async () => {
    setDownloadingSrc(true);
    try {
      const res = await api.get("/system/source-code", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "muhasabaty-source.zip";
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
      toast.success("جاري التنزيل...");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDownloadingSrc(false); }
  };

  const Toggle = ({ label, desc, k }) => (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1">
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-muted-foreground mt-1">{desc}</div>
      </div>
      <Switch checked={!!settings[k]} onCheckedChange={(v)=>setSettings({...settings, [k]: v})} data-testid={`toggle-${k}`} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground">
          <SettingsIcon className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">إعدادات النظام</h1>
          <p className="text-sm text-muted-foreground">لوحة تحكم المدير العام — مفاتيح الميزات وخيارات مخصصة تظهر بالسايدبار</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">مفاتيح الميزات</h2>
          </div>
          <Toggle label="شعار تثبيت التطبيق على شاشة الدخول" desc="إظهار QR وبطاقة التثبيت للمستخدمين الجدد" k="show_install_banner" />
          <Toggle label="المساعد الذكي (AI)" desc="إتاحة صفحة المساعد الذكي والدردشة" k="enable_ai_chat" />
          <Toggle label="مشاركة الفواتير عبر واتساب" desc="إظهار زر واتساب في صفحة الفواتير" k="enable_whatsapp" />
          <Toggle label="مسح الباركود بالكاميرا" desc="إظهار زر الكاميرا في نقطة البيع والمشتريات" k="enable_camera_scan" />
        </Card>

        <Card className="p-5">
          <h2 className="font-heading font-bold mb-3">هوية التطبيق والدعم</h2>
          <div className="space-y-3">
            <div><Label>اسم التطبيق</Label><Input value={settings.app_name||""} onChange={(e)=>setSettings({...settings, app_name: e.target.value})} data-testid="app-name-input" /></div>
            <div><Label>هاتف الدعم الفني</Label><Input value={settings.support_phone||""} onChange={(e)=>setSettings({...settings, support_phone: e.target.value})} dir="ltr" className="font-mono" /></div>
            <div><Label>إيميل الدعم الفني</Label><Input type="email" value={settings.support_email||""} onChange={(e)=>setSettings({...settings, support_email: e.target.value})} dir="ltr" className="font-mono" /></div>
            <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="w-full">
              <Save className="w-4 h-4 me-2" /> حفظ الإعدادات
            </Button>
          </div>
        </Card>
      </div>

      {canManageFeatures && (
        <Card className="p-5 border-indigo-200 bg-indigo-50/40" data-testid="company-feature-access-card">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-indigo-700" />
            <h2 className="font-heading font-bold text-indigo-950">تقييد وتفعيل ميزات الشركات</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            اختر الشركة ثم حدّد سياسة الوصول. عند التخصيص، الزر الأخضر يعني أن الميزة مفتوحة، والأحمر يعني أنها مقيدة لهذه الشركة. التغيير يُفرض على الواجهة وطلبات الخادم.
          </p>
          <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
            <div>
              <Label>الشركة</Label>
              <Select value={featureForm.company_id} onValueChange={selectFeatureCompany}>
                <SelectTrigger data-testid="feature-company-select"><SelectValue placeholder="اختر الشركة" /></SelectTrigger>
                <SelectContent>
                  {(featureAccess.companies || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveFeatureAccess} disabled={featureSaving || !featureForm.company_id} className="gap-2" data-testid="save-feature-access-button">
              {featureSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ التغييرات
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-2 mt-4" role="group" aria-label="سياسة ميزات الشركة">
            {[
              { value: "plan", title: "حسب الاشتراك", description: "استخدم ميزات الباقة الحالية" },
              { value: "custom", title: "تخصيص وتقييد", description: "فعّل أو قيّد كل ميزة بزر مستقل" },
              { value: "all", title: "فتح كل الميزات", description: "افتح كامل كتالوج الميزات لهذه الشركة" },
            ].map(option => <button
              key={option.value}
              type="button"
              onClick={() => selectFeatureMode(option.value)}
              aria-pressed={featureForm.mode === option.value}
              data-testid={`feature-mode-${option.value}`}
              className={`rounded-lg border p-3 text-start transition ${featureForm.mode === option.value ? "border-indigo-500 bg-white ring-2 ring-indigo-200" : "border-border bg-white/60 hover:bg-white"}`}
            >
              <span className="block text-sm font-bold">{option.title}</span>
              <span className="block mt-1 text-xs text-muted-foreground">{option.description}</span>
            </button>)}
          </div>
          {featureForm.mode === "custom" && (
            <div className="mt-4 rounded-lg border bg-white p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold">مفاتيح ميزات الشركة</h3>
                  <p className="text-xs text-muted-foreground">ابدأ من ميزات الاشتراك، ثم اضغط المفتاح لتغيير حالة الميزة.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold" data-testid="feature-enabled-count">
                  {featureForm.enabled.length} من {featureAccess.catalog.length} ميزة مفتوحة
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(featureAccess.catalog || []).map(f => {
                  const enabled = featureForm.enabled.includes(f.code);
                  return <div key={f.code} className={`flex items-center justify-between gap-3 rounded-md border p-3 ${enabled ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${enabled ? "bg-emerald-600" : "bg-rose-600"}`} aria-hidden="true" />
                        <span className="text-sm font-semibold">{f.name}</span>
                      </div>
                      <span className="mt-1 block text-[11px] text-muted-foreground">{f.desc}</span>
                      <span className={`mt-1 block text-[11px] font-bold ${enabled ? "text-emerald-700" : "text-rose-700"}`}>{enabled ? "مفتوحة" : "مقيدة"}</span>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={() => toggleFeature(f.code)}
                      aria-label={`${enabled ? "تقييد" : "تفعيل"} ${f.name}`}
                      data-testid={`feature-toggle-${f.code}`}
                      className={enabled ? "data-[state=checked]:bg-emerald-600" : "data-[state=unchecked]:bg-rose-600"}
                    />
                  </div>;
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">خيارات مخصصة (تظهر بالسايدبار)</h2>
          </div>
          <Button onClick={openNew} data-testid="add-menu-item-button"><Plus className="w-4 h-4 me-1" /> خيار جديد</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          أضف روابط سريعة تظهر بشريط التنقل لجميع المستخدمين — رابط خارجي (موقع/تطبيق) أو ملاحظة داخلية سريعة.
        </p>
        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-start">الترتيب</TableHead>
              <TableHead className="text-start">الأيقونة</TableHead>
              <TableHead className="text-start">العنوان</TableHead>
              <TableHead className="text-start">النوع</TableHead>
              <TableHead className="text-start">القيمة</TableHead>
              <TableHead className="text-start"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">لا توجد خيارات مخصصة بعد</TableCell></TableRow>}
              {items.map(it => (
                <TableRow key={it.id}>
                  <TableCell className="tabular-nums">{it.order || 0}</TableCell>
                  <TableCell className="font-mono text-xs">{it.icon}</TableCell>
                  <TableCell className="font-semibold">{it.label}</TableCell>
                  <TableCell><span className="text-xs">{it.url ? "رابط خارجي" : "ملاحظة"}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[240px]" dir="ltr">{it.url || it.note}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>openEdit(it)} data-testid={`edit-item-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>delItem(it.id)} className="text-destructive" data-testid={`delete-item-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="p-5 border-amber-300 bg-amber-50" data-testid="source-code-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-md bg-amber-500 grid place-items-center text-white shrink-0"><Lock className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-amber-900">الكود المصدري (خاص بك فقط)</div>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              ملف مضغوط يحتوي على كل الكود (Backend + Frontend + README + <code className="font-mono">.env.example</code>).
              محمي بصلاحية المدير العام حصراً — لا يمكن لأي زائر أو مستخدم آخر تنزيله. احتفظ به في مكان آمن.
            </p>
            <Button onClick={downloadSourceCode} disabled={downloadingSrc} className="mt-3 gap-2 bg-amber-600 hover:bg-amber-700" data-testid="download-source-btn">
              {downloadingSrc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              تحميل الكود المصدري (ZIP)
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-indigo-300 bg-indigo-50/60" data-testid="backup-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-md bg-indigo-600 grid place-items-center text-white shrink-0"><Save className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-indigo-900">النسخ الاحتياطي</div>
            <p className="text-xs text-indigo-800 mt-1 leading-relaxed">
              نسخ احتياطي <b>أوتوماتيكي كل {backups.interval_hours} ساعات</b> ويُحتفظ بآخر <b>{backups.keep} نسخ</b> لكل شركة{isRoot ? " وللمدير العام (نسخة كاملة)" : ""}. يمكنك أيضاً إنشاء نسخة الآن أو الاستعادة أو التنزيل.
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button onClick={() => runBackup("company")} disabled={bkBusy} size="sm" className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="backup-now-company">
                {bkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} نسخة الشركة الآن
              </Button>
              {isRoot && (
                <Button onClick={() => runBackup("full")} disabled={bkBusy} size="sm" variant="outline" className="gap-2 border-indigo-400 text-indigo-700" data-testid="backup-now-full">
                  <Save className="w-4 h-4" /> نسخة كاملة (المدير العام)
                </Button>
              )}
            </div>

            {[["نسخ الشركة", backups.company, true], ...(isRoot ? [["النسخ الكاملة", backups.full, false]] : [])].map(([title, list, canRestore]) => (
              <div key={title} className="mt-4">
                <div className="text-xs font-bold text-indigo-900 mb-1">{title} ({list.length})</div>
                {list.length === 0 ? (
                  <div className="text-xs text-muted-foreground">لا توجد نسخ بعد — ستُنشأ تلقائياً خلال ٣ ساعات أو اضغط «نسخة ... الآن».</div>
                ) : (
                  <div className="space-y-1.5">
                    {list.map(b => (
                      <div key={b.id} className="flex items-center gap-2 p-2 rounded-md border bg-white text-xs" data-testid={`backup-row-${b.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono truncate">{new Date(b.created_at).toLocaleString("ar-EG")}</div>
                          <div className="text-[10px] text-muted-foreground">{b.docs} سجل · {fmtSize(b.size)}</div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.id}`}><Download className="w-3.5 h-3.5" /> تنزيل</Button>
                        {canRestore && <Button size="sm" variant="outline" className="h-7 gap-1 border-amber-400 text-amber-700" onClick={() => restoreBackup(b)} disabled={bkBusy} data-testid={`backup-restore-${b.id}`}>استعادة</Button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent data-testid="menu-item-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل خيار" : "خيار جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>العنوان</Label><Input value={itemForm.label} onChange={(e)=>setItemForm({...itemForm, label:e.target.value})} data-testid="item-label-input" /></div>
            <div><Label>الأيقونة</Label>
              <Select value={itemForm.icon} onValueChange={(v)=>setItemForm({...itemForm, icon:v})}>
                <SelectTrigger data-testid="item-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AVAILABLE_ICONS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>الترتيب</Label><Input type="number" value={itemForm.order} onChange={(e)=>setItemForm({...itemForm, order:e.target.value})} /></div>
            <div className="col-span-2">
              <Label>الرابط الخارجي (اختياري)</Label>
              <Input value={itemForm.url||""} onChange={(e)=>setItemForm({...itemForm, url:e.target.value})} placeholder="https://..." dir="ltr" className="font-mono" data-testid="item-url-input" />
            </div>
            <div className="col-span-2">
              <Label>ملاحظة داخلية (تظهر عند الضغط، اختياري)</Label>
              <textarea value={itemForm.note||""} onChange={(e)=>setItemForm({...itemForm, note:e.target.value})} rows={3} className="w-full mt-1 p-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" data-testid="item-note-input" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveItem} data-testid="save-item-button">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
